/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { Map, Iterable } from 'immutable';
import isEmpty           from 'lodash/isEmpty';
import Grid from '@mui/material/Grid';

import Autocomplete from 'components/Api/Autocomplete';

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function Geolocation({
  name = null, value = null, onChange = null, disabled = false,
}) {
  /* –– Computed values
   * –––––––––––––––––––––––––––––––– */
  const apiKey = useMemo(() => (window.EXTRA_DATA || {}).PLACES_API_KEY, []);

  /**
   * This value does not update when the value prop changes because
   * the final geolocation information is abbreviated. Therefore, it's better to let the
   * Autocomplete component handle the displayed value. We only populate the value for
   * initial render.
   * e.g. -> We store PH instead of Philippines.
   * */
  const initialValue = useMemo(() => {
    const geolocation = value?.get('geolocation');

    if (isEmpty(geolocation)) { return ''; }

    const administrativeArea = [geolocation.postalCode, geolocation.state]
      .filter(Boolean)
      .join(' ');

    return [geolocation.street, geolocation.city, administrativeArea, geolocation.countryCode]
      .filter(Boolean)
      .join(', ');
  }, []);

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const onPlaceSelect = (place) => {
    if (isEmpty(place)) {
      onChange(name, new Map({ geolocation: {} }));
      return;
    }

    const parsedGeolocation = {
      latitude:    `${place.latitude}`,
      longitude:   `${place.longitude}`,
      countryCode: place.country,
      state:       place.administrative_area,
      city:        place.locality,
      postalCode:  place.postal_code,
      street:      place.street,
    };

    onChange(name, new Map({ geolocation: parsedGeolocation }));
  };

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  return (
    <Grid container>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Autocomplete
          disabled={disabled}
          onPlaceSelect={onPlaceSelect}
          apiKey={apiKey}
          name={name}
          value={initialValue}
        />
      </Grid>
    </Grid>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
Geolocation.propTypes = {
  name:     PropTypes.string,
  value:    PropTypes.objectOf(Iterable),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default Geolocation;
